import React, { ReactNode } from 'react';

import { TextProps } from '@atlaskit/primitives/compiled';

import { KnowledgeCard } from '../base-components/KnowledgeCard';
import { KnowledgeCardActions } from '../base-components/KnowledgeCardActions';
import { KnowledgeCardCategory } from '../base-components/KnowledgeCardCategory';
import { KnowledgeCardContent } from '../base-components/KnowledgeCardContent';
import { KnowledgeCardMedia } from '../base-components/KnowledgeCardMedia';
import { KnowledgeCardPrimaryDescription } from '../base-components/KnowledgeCardPrimaryDescription';
import { KnowledgeCardSecondaryDescription } from '../base-components/KnowledgeCardSecondaryDescription';

type FullStandardActionsKnowledgeCardProps = {
	size: 'full';
	category: string;
};

type CompactStandardActionsKnowledgeCardProps = {
	size: 'compact';
	category?: undefined;
};

type StandardActionsKnowledgeCardProps = {
	name: string;
	actions?: ReactNode[];
	media: ReactNode;
	primaryDescription: string;
	secondaryDescription?: string;
	primaryDescriptionColor?: TextProps['color'];
} & (FullStandardActionsKnowledgeCardProps | CompactStandardActionsKnowledgeCardProps);

/**
 * __StandardActionsKnowledgeCard__
 *
 * The StandardActionsKnowledgeCard component is a standard component used in SAIN to display information with actions.
 *
 * @example
 * ```tsx
 * <StandardActionsKnowledgeCard>
 * 	name="example-actions-card"
 * 	category="Holidays"
 * 	primaryDescription="Monday, February 17th, 2025"
 * 	secondaryDescription="President's day is a day off for workers based in the USA."
 * 	size="full"
 * 	actions={[
 * 		<Button iconBefore={CalendarIcon}>Save to Calendar</Button>,
 * 		<IconButton icon={CopyIcon} label="copy" />,
 * 	]}
 * 	media={<Image src={CalendarLight} />}
 * </StandardActionsKnowledgeCard>
 * ```
 *
 */
export const StandardActionsKnowledgeCard = ({
	name,
	actions,
	category,
	media,
	primaryDescription,
	secondaryDescription,
	size,
	primaryDescriptionColor,
}: StandardActionsKnowledgeCardProps) => {
	const isCardCompact = size === 'compact';
	const actionsToDisplay = isCardCompact && actions && actions.length > 1 ? [actions[0]] : actions;

	return (
		<KnowledgeCard name={name} space={isCardCompact ? 'space.150' : 'space.250'} size={size}>
			<KnowledgeCardMedia size={size}>{media}</KnowledgeCardMedia>
			<KnowledgeCardContent space={isCardCompact ? 'space.100' : 'space.200'}>
				<KnowledgeCardContent space={'space.100'}>
					{size === 'full' ? <KnowledgeCardCategory>{category}</KnowledgeCardCategory> : null}
					<KnowledgeCardContent space={isCardCompact ? 'space.0' : 'space.050'}>
						<KnowledgeCardPrimaryDescription size={size} color={primaryDescriptionColor}>
							{primaryDescription}
						</KnowledgeCardPrimaryDescription>
						{secondaryDescription ? (
							<KnowledgeCardSecondaryDescription
								color={isCardCompact ? 'color.text.subtlest' : 'color.text.subtle'}
							>
								{secondaryDescription}
							</KnowledgeCardSecondaryDescription>
						) : null}
					</KnowledgeCardContent>
				</KnowledgeCardContent>
				{actionsToDisplay && (
					<KnowledgeCardActions>
						{actionsToDisplay.map((action, index) => (
							<React.Fragment key={`displayed-action-${index}`}>{action}</React.Fragment>
						))}
					</KnowledgeCardActions>
				)}
			</KnowledgeCardContent>
		</KnowledgeCard>
	);
};
